import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Accordion, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { fetchFilters, setActivePanel } from './store/assetLibraryFilter.actions';
import { AssetLibraryFilterState, Filter } from './store/assetLibraryFilter.types';
import { AssetLibraryDispatch } from './store/assetLibraryFilter.types';
import FilterTypeRow from './FilterTypeRow';
import FilterPanel from './FilterPanel';
import Loader from '../../../Components/Utility/Loader/Loader';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import './AssetLibrary.css';

interface AssetLibraryRootState {
  AssetLibraryFilters: AssetLibraryFilterState;
  Users: { logInRights: unknown };
}

function AssetLibraryTab(): JSX.Element {
  const dispatch = useDispatch<AssetLibraryDispatch>();
  const { t } = useTranslation('systemMaster');
  const { CheckActionPermission } = useComponent();
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const { filters, loading, activePanel } = useSelector((state: AssetLibraryRootState) => state.AssetLibraryFilters);
  const { logInRights } = useSelector((state: AssetLibraryRootState) => state.Users);

  type CheckPermFn = (rights: unknown, section: string, module: string, action: string) => boolean;
  const checkPerm = CheckActionPermission as unknown as CheckPermFn;
  const masterAddEditAccess: boolean = checkPerm(logInRights, 'masters', 'assetLibrary', 'addEdit');
  const masterDeleteAccess: boolean = checkPerm(logInRights, 'masters', 'assetLibrary', 'delete');

  useEffect(() => {
    dispatch(fetchFilters());
  }, [dispatch]);

  const handleSettingsClick = (filterTypeId: string): void => {
    dispatch(setActivePanel(activePanel === filterTypeId ? null : filterTypeId));
  };

  const handleClosePanel = (): void => {
    dispatch(setActivePanel(null));
  };

  const activeFilter: Filter | undefined = filters.find((f) => f._id === activePanel);

  return (
    <>
      <Accordion className="mb-4 asset-library-filters-accordion">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>{t('ASSET_LIBRARY.FILTERS_ACCORDION')}</p>
            </div>
            <div className="action-title-btns single-btn">
              <CustomTooltip tooltipText={t(showFilters ? 'CUSTOM_ATTRIBUTES.CLOSE' : 'CUSTOM_ATTRIBUTES.OPEN')}>
                <button className="btn d-inline-flex p-0" type="button" onClick={() => setShowFilters(!showFilters)}>
                  {showFilters ? <i className="icon-new-down-arrow-icon icon-inverse f-16" /> : <i className="icon-new-down-arrow-icon  f-16" />}
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {showFilters && (
            <Card.Body>
              {loading ? (
                <Loader />
              ) : (
                <PerfectScrollbar>
                  <ul>
                    {filters.map((filter) => (
                      <FilterTypeRow key={filter._id} filter={filter} onSettingsClick={handleSettingsClick} masterAddEditAccess={masterAddEditAccess} />
                    ))}
                  </ul>
                </PerfectScrollbar>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>

      {activePanel && activeFilter && (
        <FilterPanel filter={activeFilter} onClose={handleClosePanel} masterAddEditAccess={masterAddEditAccess} masterDeleteAccess={masterDeleteAccess} />
      )}
    </>
  );
}

export default AssetLibraryTab;
